import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import { CheckCircleFilled, CrownOutlined, PlayCircleOutlined } from '@ant-design/icons';

import { Button, Checkbox, Dialog, Field, Select, Textarea } from '../../../../components/ui';
import { useLoi } from '../../../../hooks/useLoi';
import { useT } from '../../../../i18n';
import { soVN } from '../../../../lib/dinh-dang';
import type { KhoaDich } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { useToast } from '../../../../lib/toast';
import { CanhBaoTrongHop, LoiTrongHop } from '../../../tenant/components/phan-form';
import { LOAI_LICENSE_HIEN, QUYEN, TRANG_THAI_LICENSE_HIEN } from '../../constants';
import { useCapLicense, useSuaLicense } from '../../hooks/useLicense';
import {
  formTuLicense,
  homNay,
  kiemFormLicense,
  tenProjectLicense,
  thanCap,
  thanSua,
  trangThaiUser,
  TRANG_THAI_KHI_CAP,
  TRANG_THAI_KHI_SUA,
  type FormLicense,
  type LoiFormLicense,
} from '../../nghiep-vu/license';
import type { ApiError } from '../../../../lib/errors';
import type { LicenseRpa, LoaiLicense, ProjectOpenRpa, UserOpenCore } from '../../types';
import { BadgeTrangThaiLicense } from './nhan';

const NHAN_LOI: Record<LoiFormLicense, KhoaDich> = {
  userRequired: 'rpaLicense.form.loiUser',
  projectRequired: 'rpaLicense.form.loiProject',
  workflowsRequired: 'rpaLicense.form.loiWorkflow',
  expiryRequired: 'rpaLicense.form.loiHan',
  activeNeedsFuture: 'rpaLicense.form.loiHanActive',
  duplicate: 'rpaLicense.form.loiTrung',
};

const LOAI: { loai: LoaiLicense; icon: ReactNode }[] = [
  { loai: 'limited', icon: <PlayCircleOutlined aria-hidden /> },
  { loai: 'pro', icon: <CrownOutlined aria-hidden /> },
];

const nhanUser = (u: UserOpenCore) => {
  const ten = u.username || u.name || u.id;
  return u.email && u.email !== ten ? `${ten} — ${u.email}` : ten;
};

/**
 * Cấp License cho một User OpenCore hoặc sửa License đã có (`LicenseFormDialog` của native). Không có ô nhập khoá: máy
 * chủ tự sinh mã nội bộ. License cũ (không có phạm vi Workflow) giữ nguyên phạm vi khi sửa. Lưu xong máy chủ đồng bộ
 * role OpenCore ngay; đồng bộ hỏng thì vẫn đã lưu, toast nói rõ.
 */
export function HopLicense({
  goc,
  userSan,
  licenses,
  users,
  loiUsers,
  projects,
  loiProjects,
  dangTaiProjects,
  thieuQuyenProject,
  onXem,
  onClose,
}: {
  goc: LicenseRpa | null;
  userSan: string;
  licenses: readonly LicenseRpa[];
  users: readonly UserOpenCore[];
  loiUsers: string | null;
  projects: readonly ProjectOpenRpa[];
  loiProjects: ApiError | null;
  dangTaiProjects: boolean;
  thieuQuyenProject: boolean;
  onXem: (l: LicenseRpa) => void;
  onClose: () => void;
}) {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const formId = useId();
  const cap = useCapLicense();
  const sua = useSuaLicense();
  const dang = goc ? sua : cap;
  const taoMoi = !goc;
  const legacy = Boolean(goc && !goc.workflow_scope);
  const [v, setV] = useState<FormLicense>(() => formTuLicense(goc, userSan));
  const [loi, setLoi] = useState<LoiFormLicense | null>(null);

  const dat = <K extends keyof FormLicense>(k: K, gt: FormLicense[K]) => {
    setV((cu) => ({ ...cu, [k]: gt }));
    setLoi(null);
    dang.reset();
  };

  const coLicense = new Set(licenses.map((l) => l.opencore_user_id).filter(Boolean));
  // User chưa có License lên đầu danh sách chọn.
  const sapXep = [...users].sort((a, b) => Number(coLicense.has(a.id)) - Number(coLicense.has(b.id)));
  const luaChonUser = [
    ...(goc?.opencore_user_id && !users.some((u) => u.id === goc.opencore_user_id)
      ? [{ value: goc.opencore_user_id, label: goc.opencore_username || goc.opencore_user_id }]
      : []),
    ...sapXep.map((u) => ({
      value: u.id,
      label: coLicense.has(u.id) && u.id !== goc?.opencore_user_id ? `${nhanUser(u)} ${t('rpaLicense.form.daCoLicense')}` : nhanUser(u),
    })),
  ];
  const project = projects.find((p) => p.id === v.projectId);
  const licenseCuaUser = taoMoi && v.userId ? licenses.filter((l) => l.opencore_user_id === v.userId) : [];

  const gui = () => {
    const thay = kiemFormLicense(v, { goc, legacy, licenses });
    setLoi(thay);
    if (thay || dang.isPending) return;
    const xong = (l: LicenseRpa) => {
      const hongDongBo = l.access_sync_status === 'error';
      toast.success(t(taoMoi ? (hongDongBo ? 'rpaLicense.form.daCapHongDongBo' : 'rpaLicense.form.daCap') : hongDongBo ? 'rpaLicense.form.daLuuHongDongBo' : 'rpaLicense.form.daLuu'));
      onClose();
    };
    if (goc) sua.mutate({ id: goc.id, than: thanSua(v, goc, legacy) }, { onSuccess: xong });
    else cap.mutate(thanCap(v, homNay()), { onSuccess: xong });
  };

  const loiO = (o: LoiFormLicense[]) => (loi && o.includes(loi) ? t(NHAN_LOI[loi]) : undefined);

  return (
    <Dialog
      open
      onClose={onClose}
      width={760}
      destroyOnHidden
      title={taoMoi ? t('rpaLicense.form.capTieuDe') : t('rpaLicense.form.suaTieuDe')}
      footer={
        <div className="space-y-3">
          {dang.error && <LoiTrongHop>{dichLoi(dang.error)}</LoiTrongHop>}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="min-w-0 flex-1 text-xs text-content-muted">{t('rpaLicense.form.chanGoiY')}</p>
            <div className="flex flex-wrap justify-end gap-3">
              <Button variant="outline" size="md" onClick={onClose}>
                {t('chung.huy')}
              </Button>
              <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dang.isPending}>
                {dang.isPending ? t('chung.dangLuu') : t(taoMoi ? 'rpaLicense.form.cap' : 'rpaLicense.form.luu')}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-4 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <p className="text-sm text-content-secondary">{t(taoMoi ? 'rpaLicense.form.gioiThieuCap' : 'rpaLicense.form.gioiThieuSua')}</p>

        <PhanBuoc so={1} tieuDe={t('rpaLicense.form.phanDoiTuong')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('rpaLicense.form.user')} required error={loiO(['userRequired', 'duplicate'])} hint={loiUsers ?? undefined}>
              {(p) => (
                <Select<string>
                  id={p.id}
                  aria-invalid={p['aria-invalid']}
                  aria-describedby={p['aria-describedby']}
                  status={p['aria-invalid'] ? 'error' : undefined}
                  className="w-full"
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('rpaLicense.form.chonUser')}
                  value={v.userId || undefined}
                  onChange={(x) => dat('userId', x)}
                  options={luaChonUser}
                />
              )}
            </Field>
            <Field
              label={t('rpaLicense.form.project')}
              required={taoMoi}
              error={loiO(['projectRequired'])}
              hint={
                thieuQuyenProject
                  ? t('rpaLicense.form.thieuQuyenProject', { quyen: QUYEN.projectDoc })
                  : loiProjects
                    ? dichLoi(loiProjects)
                    : t('rpaLicense.form.projectGoiY')
              }
            >
              {(p) => (
                <Select<string>
                  id={p.id}
                  aria-invalid={p['aria-invalid']}
                  aria-describedby={p['aria-describedby']}
                  status={p['aria-invalid'] ? 'error' : undefined}
                  className="w-full"
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('rpaLicense.form.chonProject')}
                  disabled={!taoMoi}
                  loading={dangTaiProjects}
                  value={v.projectId || undefined}
                  onChange={(x) => setV((cu) => ({ ...cu, projectId: x, workflowIds: [] }))}
                  options={
                    taoMoi
                      ? projects.map((pr) => ({ value: pr.id, label: t('rpaLicense.form.projectNhan', { ten: pr.name, so: soVN(pr.workflows.length, locale) }) }))
                      : [{ value: v.projectId, label: goc ? tenProjectLicense(goc) : v.projectId }]
                  }
                />
              )}
            </Field>
          </div>

          <fieldset className="mt-4 min-w-0">
            <legend className="mb-2 text-sm font-medium text-content-primary">
              {t('rpaLicense.form.workflow')}
              {!legacy && (
                <span className="ml-0.5 text-danger-700" aria-hidden>
                  *
                </span>
              )}
            </legend>
            {legacy ? (
              <p className="text-sm text-content-muted">{t('rpaLicense.form.phamViCu')}</p>
            ) : project ? (
              <ChonWorkflow project={project} chon={v.workflowIds} onDoi={(ids) => dat('workflowIds', ids)} />
            ) : (
              <p className="text-sm text-content-muted">
                {dangTaiProjects && v.projectId
                  ? t('rpaLicense.form.dangTaiWorkflow')
                  : v.projectId
                    ? t('rpaLicense.form.projectMat')
                    : t('rpaLicense.form.chonProjectTruoc')}
              </p>
            )}
            {loi === 'workflowsRequired' && (
              <p role="alert" className="mt-1 text-xs text-danger-700">
                {t(NHAN_LOI.workflowsRequired)}
              </p>
            )}
          </fieldset>

          {licenseCuaUser.length > 0 && (
            <div className="mt-3">
              <CanhBaoTrongHop>
                <p>{t('rpaLicense.form.userDaCo', { so: licenseCuaUser.length })}</p>
                <ul className="mt-1 space-y-1">
                  {licenseCuaUser.map((l) => (
                    <li key={l.id} className="flex flex-wrap items-center gap-2">
                      <span>{tenProjectLicense(l)}</span>
                      <BadgeTrangThaiLicense status={trangThaiUser(l)} />
                      <Button variant="ghost" size="sm" onClick={() => onXem(l)}>
                        {t('rpaLicense.xemLicense')}
                      </Button>
                    </li>
                  ))}
                </ul>
              </CanhBaoTrongHop>
            </div>
          )}
        </PhanBuoc>

        <PhanBuoc so={2} tieuDe={t('rpaLicense.form.phanLoai')}>
          <div role="group" aria-label={t('rpaLicense.form.phanLoai')} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {LOAI.map(({ loai, icon }) => {
              const chon = v.tier === loai;
              return (
                <button
                  key={loai}
                  type="button"
                  aria-pressed={chon}
                  onClick={() => dat('tier', loai)}
                  className={cn(
                    'relative flex items-start gap-3 rounded-lg border p-3 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                    chon ? 'border-primary-500 bg-primary-50' : 'border-default bg-surface hover:bg-subtle',
                  )}
                >
                  <span className={cn('mt-0.5 text-lg', chon ? 'text-primary-600' : 'text-content-muted')}>{icon}</span>
                  <span className="min-w-0 pr-5">
                    <span className="block text-sm font-semibold text-content-primary">{t(LOAI_LICENSE_HIEN[loai].ten)}</span>
                    <span className="block text-xs text-content-secondary">{t(LOAI_LICENSE_HIEN[loai].moTa)}</span>
                  </span>
                  {chon && <CheckCircleFilled aria-hidden className="absolute right-3 top-3 text-primary-600" />}
                </button>
              );
            })}
          </div>
        </PhanBuoc>

        <PhanBuoc so={3} tieuDe={t('rpaLicense.form.phanHan')}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('rpaLicense.form.ngayHet')} required={!v.lifetime} error={loiO(['expiryRequired', 'activeNeedsFuture'])}>
              {(p) => (
                <DatePicker
                  id={p.id}
                  aria-invalid={p['aria-invalid']}
                  aria-describedby={p['aria-describedby']}
                  status={p['aria-invalid'] ? 'error' : undefined}
                  className="w-full"
                  format="DD/MM/YYYY"
                  disabled={v.lifetime}
                  value={v.expiry ? dayjs(v.expiry) : null}
                  onChange={(d) => dat('expiry', d ? d.format('YYYY-MM-DD') : '')}
                />
              )}
            </Field>
            <Field label={t('rpaLicense.form.trangThai')}>
              {(p) => (
                <Select
                  id={p.id}
                  className="w-full"
                  value={v.status}
                  onChange={(x) => dat('status', x)}
                  options={(taoMoi ? TRANG_THAI_KHI_CAP : TRANG_THAI_KHI_SUA).map((s) => ({ value: s, label: t(TRANG_THAI_LICENSE_HIEN[s].khoa) }))}
                />
              )}
            </Field>
          </div>
          <Checkbox className="mt-3" checked={v.lifetime} onChange={(e) => dat('lifetime', e.target.checked)}>
            {t('rpaLicense.form.vinhVien')}
          </Checkbox>
        </PhanBuoc>

        <Field label={t('rpaLicense.form.ghiChu')}>
          {(p) => (
            <Textarea
              {...p}
              rows={2}
              maxLength={1000}
              placeholder={t('rpaLicense.form.ghiChuGoiY')}
              value={v.note}
              onChange={(e) => dat('note', e.target.value)}
            />
          )}
        </Field>
        {goc?.access_sync_error && (
          <p className="text-xs text-warning-700">
            {t('rpaLicense.form.loiDongBoCu')} {goc.access_sync_error}
          </p>
        )}
      </form>
    </Dialog>
  );
}

function PhanBuoc({ so, tieuDe, children }: { so: number; tieuDe: string; children: ReactNode }) {
  return (
    <section className="rounded-lg border border-default bg-subtle p-4">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-content-primary">
        <span aria-hidden className="flex size-5 items-center justify-center rounded-full bg-primary-600 text-xs text-content-inverse">
          {so}
        </span>
        {tieuDe}
      </h3>
      {children}
    </section>
  );
}

/** Tick Workflow của Project OpenRPA mà User được dùng; License chỉ xem / chạy được đúng các Workflow này. */
function ChonWorkflow({ project, chon, onDoi }: { project: ProjectOpenRpa; chon: string[]; onDoi: (ids: string[]) => void }) {
  const { t, locale } = useT();
  const tatCa = project.workflows.map((w) => w.id);
  if (!tatCa.length) return <p className="text-sm text-content-muted">{t('rpaLicense.form.projectChuaCoWorkflow')}</p>;
  const du = chon.length === tatCa.length;
  return (
    <div>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs text-content-muted">
          {t('rpaLicense.form.daChon', { chon: soVN(chon.length, locale), tong: soVN(tatCa.length, locale) })}
        </span>
        <Button variant="ghost" size="sm" onClick={() => onDoi(du ? [] : tatCa)}>
          {du ? t('rpaLicense.form.boChonHet') : t('rpaLicense.form.chonHet')}
        </Button>
      </div>
      <ul className="max-h-56 space-y-1 overflow-y-auto rounded-lg border border-default bg-surface p-2">
        {project.workflows.map((w) => (
          <li key={w.id}>
            <Checkbox checked={chon.includes(w.id)} onChange={(e) => onDoi(e.target.checked ? [...chon, w.id] : chon.filter((x) => x !== w.id))}>
              <span className="text-sm text-content-primary">{w.name}</span>
            </Checkbox>
          </li>
        ))}
      </ul>
    </div>
  );
}
